export type EditorMode = "simple" | "advanced";
export type StudioTheme = "light" | "dark";

// StyleStore.purge() clears style data only, so these settings survive it.
// index.html reads THEME_KEY and the system scheme before first paint; keep
// the two in sync.
const MODE_KEY = "atlas-studio:mode";
const THEME_KEY = "atlas-studio:theme";
const HINT_KEY = "atlas-studio:hint";

const MODES: readonly EditorMode[] = ["simple", "advanced"];
const THEMES: readonly StudioTheme[] = ["light", "dark"];
const SYSTEM_DARK = "(prefers-color-scheme: dark)";

// The stored value, or null when nothing valid is stored.
function stored<T extends string>(key: string, allowed: readonly T[]): T | null {
  try {
    const value = window.localStorage.getItem(key);
    return allowed.includes(value as T) ? (value as T) : null;
  } catch (err) {
    return null;
  }
}

function read<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  return stored(key, allowed) ?? fallback;
}

function systemScheme(): MediaQueryList | null {
  try {
    return window.matchMedia ? window.matchMedia(SYSTEM_DARK) : null;
  } catch (err) {
    return null;
  }
}

function write(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch (err) {
    // Storage is blocked. The choice still applies until the page reloads.
  }
}

export function getEditorMode(): EditorMode {
  return read(MODE_KEY, MODES, "simple");
}

export function setEditorMode(mode: EditorMode) {
  write(MODE_KEY, mode);
}

// The theme the user chose. Until they choose, the operating system's.
export function getTheme(): StudioTheme {
  return (
    stored(THEME_KEY, THEMES) ?? (systemScheme()?.matches ? "dark" : "light")
  );
}

export function setTheme(theme: StudioTheme) {
  write(THEME_KEY, theme);
  document.documentElement.dataset.theme = theme;
}

// While the user has not chosen a theme, the page follows the operating
// system as it switches between light and dark. Returns how to stop.
export function followSystemTheme(onChange: (theme: StudioTheme) => void) {
  const scheme = systemScheme();
  if (!scheme) return () => {};

  const listener = () => {
    if (stored(THEME_KEY, THEMES) !== null) return;
    const theme: StudioTheme = scheme.matches ? "dark" : "light";
    document.documentElement.dataset.theme = theme;
    onChange(theme);
  };
  scheme.addEventListener("change", listener);
  return () => scheme.removeEventListener("change", listener);
}

// The one-line hint shown on a first visit to Simple mode.
export function isHintDismissed(): boolean {
  return stored(HINT_KEY, ["dismissed"]) === "dismissed";
}

export function dismissHint() {
  write(HINT_KEY, "dismissed");
}
